/**
 * /accounting/payroll-runs/$id — read-only payroll register for the books
 * (2026-10 persona round). Lets the accountant tie the salary journal out
 * against the locked run: headcount, gross, each deduction, net and the
 * per-employee rows. No bank details or PAN, and nothing can be changed
 * here — payroll stays with HR.
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toApiError } from "@/lib/api";
import { formatAccountsAmount } from "@/lib/accounting-gst";
import { usePayrollRegisterRun } from "@/hooks/use-accounting";
import type { PayrollRegisterTotals } from "@/types/accounting";
import { useSmPlus } from "@/lib/use-sm-plus";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/accounting/payroll-runs/$id")({
  component: PayrollRegisterRoute,
});

const COLUMNS: { key: keyof PayrollRegisterTotals; label: string }[] = [
  { key: "gross_minor", label: "Gross" },
  { key: "pf_minor", label: "PF" },
  { key: "esi_minor", label: "ESI" },
  { key: "pt_minor", label: "PT" },
  { key: "tds_minor", label: "TDS" },
  { key: "advance_recovery_minor", label: "Advances / Loans" },
  { key: "other_deductions_minor", label: "Other Deductions" },
  { key: "net_minor", label: "Net Pay" },
];

/** Phones: one row per employee — name and net pay on top, gross and each
 *  non-zero deduction underneath (the 9-column table cut off Net Pay). */
function RegisterRow({
  name,
  sub,
  values,
  total = false,
}: Readonly<{
  name: string;
  sub?: string;
  values: PayrollRegisterTotals;
  total?: boolean;
}>) {
  const details = COLUMNS.filter(
    (c) => c.key !== "net_minor" && (c.key === "gross_minor" || values[c.key] !== 0),
  );
  return (
    <li className={`space-y-1.5 px-3 py-2.5 text-sm ${total ? "bg-muted/40" : ""}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 break-words font-medium text-gray-900">
          {name}
          {sub && <span className="block text-xs font-normal text-muted-foreground">{sub}</span>}
        </span>
        <span className="shrink-0 text-right">
          <span className="block text-xs text-muted-foreground">Net Pay</span>
          <span className="whitespace-nowrap font-semibold tabular-nums">
            {formatAccountsAmount(values.net_minor)}
          </span>
        </span>
      </div>
      <dl className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {details.map((c) => (
          <div key={c.key} className="flex gap-1 whitespace-nowrap">
            <dt>{c.label}</dt>
            <dd className="tabular-nums text-gray-900">{formatAccountsAmount(values[c.key])}</dd>
          </div>
        ))}
      </dl>
    </li>
  );
}

function PayrollRegisterRoute() {
  const { tenantSlug, id } = useParams({ strict: false }) as { tenantSlug: string; id: string };
  const q = usePayrollRegisterRun(Number(id));
  const run = q.data;
  const isSmPlus = useSmPlus();

  return (
    <AccountingModuleShell
      title={run ? `Payroll Register — ${run.period_label}` : "Payroll Register"}
      description="Read-only. The figures the salary journal was posted from."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/accounting/journals" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Journals
          </Link>
        </Button>
      }
    >
      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load the payroll register</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}
      {q.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      ) : run ? (
        <div className="space-y-4">
          <Card>
            <CardContent className="grid gap-3 py-4 text-sm sm:grid-cols-4">
              <div>
                <p className="text-muted-foreground">Employees</p>
                <p className="text-lg font-semibold">{run.totals.headcount}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Gross</p>
                <p className="text-lg font-semibold tabular-nums">{formatAccountsAmount(run.totals.gross_minor)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Deductions</p>
                <p className="text-lg font-semibold tabular-nums">
                  {formatAccountsAmount(run.totals.total_deductions_minor)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Net pay</p>
                <p className="text-lg font-semibold tabular-nums">{formatAccountsAmount(run.totals.net_minor)}</p>
              </div>
              <p className="text-xs text-muted-foreground sm:col-span-4">
                {run.run_type === "supplementary" ? "Correction run. " : ""}
                {run.journal_entry_id ? (
                  <>
                    Posted to the books as{" "}
                    <Link
                      to="/app/$tenantSlug/accounting/journals/$id"
                      params={{ tenantSlug, id: String(run.journal_entry_id) }}
                      className="font-medium text-primary underline-offset-4 hover:underline"
                    >
                      journal #{run.entry_no}
                    </Link>
                    .
                  </>
                ) : (
                  "Not posted to the books yet."
                )}{" "}
                Employer PF / ESI contributions are not part of this register.
              </p>
            </CardContent>
          </Card>
          {!isSmPlus ? (
            <ul className="divide-y rounded-md border" data-testid="payroll-register-cards">
              {(run.rows ?? []).map((r) => (
                <RegisterRow
                  key={r.employee_id}
                  name={r.name}
                  sub={r.designation || undefined}
                  values={r}
                />
              ))}
              <RegisterRow name="Total" values={run.totals} total />
            </ul>
          ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  {COLUMNS.map((c) => (
                    <TableHead key={c.key} className="text-right">
                      {c.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(run.rows ?? []).map((r) => (
                  <TableRow key={r.employee_id}>
                    <TableCell>
                      {r.name}
                      {r.designation && <span className="block text-xs text-muted-foreground">{r.designation}</span>}
                    </TableCell>
                    {COLUMNS.map((c) => (
                      <TableCell key={c.key} className="text-right tabular-nums">
                        {formatAccountsAmount(r[c.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell>Total</TableCell>
                  {COLUMNS.map((c) => (
                    <TableCell key={c.key} className="text-right tabular-nums">
                      {formatAccountsAmount(run.totals[c.key])}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </div>
          )}
        </div>
      ) : null}
    </AccountingModuleShell>
  );
}
